import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, IconButton, MenuItem,
  Paper, Select, Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import TagIcon from '@mui/icons-material/Tag';
import Send from '@mui/icons-material/Send';
import SegmentationPlayer, { SEGMENTATION_LABELS, ANNOTATION_LABELS, DEFAULT_SEGMENT_DURATION } from '@/components/WaveformPlayer/SegmentationPlayer';
import { getSegments } from '@/api/segmentationApi';
import { getAllTagsForWorkflow } from '@/services/qc/workflowTagsService';

/** Shared slotProps for all SegmentationTool dialogs — matches QcAssessmentModal exactly. */
const DIALOG_SLOT_PROPS = {
  root: {
    sx: (theme) => ({ zIndex: theme.zIndex.modal + 100_000 }),
  },
  backdrop: {
    sx: (theme) => ({
      bgcolor:
        theme.palette.mode === 'dark'
          ? 'rgba(0, 0, 0, 0.72)'
          : 'rgba(13, 28, 46, 0.35)',
    }),
  },
  paper: {
    sx: (theme) => ({
      m: 0,
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      minHeight: '100dvh',
      maxHeight: '100dvh',
      borderRadius: 0,
      overflow: 'hidden',
      bgcolor:
        theme.palette.mode === 'dark'
          ? theme.palette.background.default
          : '#f8f9ff',
      boxShadow: 'none',
      display: 'flex',
      flexDirection: 'column',
    }),
  },
};

function SegHeader({ title, fileName, state, district, language, assignedTo, onClose }) {
  return (
    <Box
      component="header"
      className="flex shrink-0 items-center justify-between border-b border-[rgba(197,197,212,0.3)] bg-white px-3 py-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-900"
    >
      <Box className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
        <Typography
          className="font-[Manrope,Inter,sans-serif] text-lg font-extrabold tracking-tighter text-[#24389c] dark:text-[#9aacf0]"
          sx={{ fontFamily: 'Manrope, Inter, sans-serif', fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1.05rem', md: '1.125rem' } }}
        >
          {title}
        </Typography>

        <Box className="hidden h-4 w-px bg-[rgba(197,197,212,0.3)] dark:bg-gray-600 md:block" />

        <Box className="hidden flex-wrap items-center gap-4 text-[11px] md:flex">
          {fileName && (
            <Tooltip title={fileName} enterDelay={400}>
              <Box className="flex gap-1 items-center">
                <TagIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">File:</span>
                <span className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-[#0d1c2e] dark:text-gray-100">
                  {fileName}
                </span>
              </Box>
            </Tooltip>
          )}
          {state && (
            <Box className="flex gap-1 items-center">
              <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">State:</span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">{state}</span>
            </Box>
          )}
          {district && (
            <Box className="flex gap-1 items-center">
              <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">Dist:</span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">{district}</span>
            </Box>
          )}
          {language && (
            <Box className="flex gap-1 items-center">
              <LanguageIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">Lang:</span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">{language}</span>
            </Box>
          )}
          {assignedTo && (
            <Box className="flex gap-1 items-center">
              <PersonIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">Assignee:</span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">{assignedTo}</span>
            </Box>
          )}
        </Box>

        {/* Mobile: compact metadata */}
        <Stack direction="row" spacing={0.75} alignItems="center" className="flex md:hidden" flexWrap="wrap">
          {fileName && (
            <Tooltip title={fileName} enterDelay={400}>
              <Typography variant="caption" noWrap sx={{ maxWidth: 120, color: 'text.secondary', fontSize: '0.65rem' }}>
                {fileName}
              </Typography>
            </Tooltip>
          )}
          {language && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{language}</Typography>
          )}
        </Stack>
      </Box>

      <Box className="flex shrink-0 items-center gap-1">
        <Box className="mx-1 h-4 w-px bg-[rgba(197,197,212,0.3)] dark:bg-gray-600" />
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          className="text-[rgba(69,70,82,0.7)] hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950/40 dark:hover:text-red-400"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function SegFooter({ readOnly, reviewMode, submitting, isPendingReview, onClose, onSaveLater, onSubmitNext, onSubmitClose, onApprove, onApproveNext, onReject }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (readOnly && !reviewMode) {
    return (
      <Box
        component="footer"
        className="flex shrink-0 items-center justify-end border-t border-[rgba(197,197,212,0.3)] bg-white px-4 py-2.5 shadow-[0_-1px_4px_rgba(0,0,0,0.04)] dark:border-gray-700 dark:bg-gray-900"
      >
        <Button
          className="ml-auto rounded bg-[#24389c] px-6 py-2 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[#1e3a8a]"
          sx={{ bgcolor: '#24389c', '&:hover': { bgcolor: '#1e3a8a' } }}
          onClick={onClose}
        >
          Close
        </Button>
      </Box>
    );
  }

  if (reviewMode) {
    return (
      <Box
        component="footer"
        className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[rgba(197,197,212,0.3)] bg-white px-4 py-2.5 shadow-[0_-1px_4px_rgba(0,0,0,0.04)] dark:border-gray-700 dark:bg-gray-900"
      >
        <Button
          onClick={onClose}
          className="text-xs font-bold uppercase tracking-wider text-[#454652] hover:bg-black/5 dark:text-gray-200 dark:hover:bg-white/10"
        >
          Cancel
        </Button>
        <Box className="flex flex-wrap items-center gap-2">
          <Button
            variant="outlined"
            color="success"
            disabled={isPendingReview}
            onClick={onApproveNext}
            className="rounded-md px-4 py-2 text-[11px] font-bold uppercase tracking-wider"
          >
            Approve &amp; Next
          </Button>
          <Button
            variant="outlined"
            color="success"
            disabled={isPendingReview}
            onClick={onApprove}
            className="rounded-md px-4 py-2 text-[11px] font-bold uppercase tracking-wider"
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={isPendingReview}
            onClick={onReject}
            className="rounded-md px-4 py-2 text-[11px] font-bold uppercase tracking-wider"
          >
            Reject
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[rgba(197,197,212,0.3)] bg-white px-4 py-2.5 shadow-[0_-1px_4px_rgba(0,0,0,0.04)] dark:border-gray-700 dark:bg-gray-900"
    >
      <Button
        onClick={onClose}
        className="text-xs font-bold uppercase tracking-wider text-[#454652] hover:bg-black/5 dark:text-gray-200 dark:hover:bg-white/10"
      >
        Cancel
      </Button>
      <Box className="flex flex-wrap items-center gap-2 md:gap-3">
        <Button
          variant="outlined"
          className="rounded-md border border-[rgba(36,56,156,0.2)] px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#24389c] hover:bg-[rgba(36,56,156,0.05)] dark:border-[rgba(139,159,232,0.45)] dark:text-[#b4c2f5] dark:hover:bg-white/10"
          onClick={onSaveLater}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : isMobile ? 'Save Later' : 'Save for Later & Next'}
        </Button>
        <Button
          variant="outlined"
          className="rounded-md border border-[rgba(36,56,156,0.2)] px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#24389c] hover:bg-[rgba(36,56,156,0.05)] dark:border-[rgba(139,159,232,0.45)] dark:text-[#b4c2f5] dark:hover:bg-white/10"
          onClick={onSubmitNext}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : isMobile ? 'Submit Next' : 'Submit & Next'}
        </Button>
        <Button
          variant="contained"
          endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send sx={{ fontSize: 18 }} />}
          className="rounded bg-[#24389c] px-6 py-2 text-[11px] font-bold uppercase tracking-widest text-white shadow-sm hover:bg-[#1e3a8a] hover:shadow"
          sx={{ bgcolor: '#24389c', '&:hover': { bgcolor: '#1e3a8a' } }}
          onClick={onSubmitClose}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : isMobile ? 'Submit' : 'Submit & Close'}
        </Button>
      </Box>
    </Box>
  );
}

export default function SegmentationTool({
  open,
  onClose,
  audioFileURL,
  fileName,
  state,
  district,
  language,
  assignedToName,
  onSaveLater,
  onSubmitNext,
  onSubmitClose,
  submitting = false,
  labels,
  tagOptions,
  showLabels = false,
  title = 'Annotate Audio',
  initialSegments = [],
  transcription = '',
  transcription2 = '',
  updatedTranscription = '',
  initialComment = '',
  workflowId,
  readOnly = false,
  reviewMode = false,
  onApprove,
  onApproveNext,
  onReject,
  isPendingReview = false,
}) {
  const [segments, setSegments] = useState([]);
  const [transcript, setTranscript] = useState(updatedTranscription || transcription);
  const [comment, setComment] = useState(initialComment);
  const [selectedTag, setSelectedTag] = useState('');
  const [playerKey, setPlayerKey] = useState(0);
  const [autoSegments, setAutoSegments] = useState(null);
  const [confirmAutoFillOpen, setConfirmAutoFillOpen] = useState(false);
  const [tagMismatchError, setTagMismatchError] = useState(null);
  const textFieldRef = useRef(null);
  const savedSelectionRef = useRef({ start: 0, end: 0 });

  const { data: tagsData = [] } = useQuery({
    queryKey: ['seg-tool-tags', workflowId],
    queryFn: () => getAllTagsForWorkflow(workflowId),
    enabled: !!workflowId,
    staleTime: Infinity,
  });

  const transcriptionTags = useMemo(() => {
    const extractTags = (text) => {
      if (!text) return [];
      const angle = [...text.matchAll(/<([^/>][^>]*)>/g)].map((m) => m[1].trim());
      const square = [...text.matchAll(/\[([^\]]+)\]/g)].map((m) => m[1].trim());
      return [...angle, ...square];
    };
    return [...new Set([...extractTags(transcription), ...extractTags(updatedTranscription)])];
  }, [transcription, updatedTranscription]);

  const allTagOptions = useMemo(() => {
    const fromApi = Array.isArray(tagsData) ? tagsData.map((t) => t.tag_name).filter(Boolean) : [];
    return [...new Set([...transcriptionTags, ...fromApi])];
  }, [transcriptionTags, tagsData]);

  const defaultLabel = useMemo(
    () => (labels ?? []).find((l) => l.isDefault)?.value ?? labels?.[0]?.value ?? 'NOISE',
    [labels],
  );

  function handleClose() {
    setSegments([]);
    setTranscript(updatedTranscription || transcription);
    setComment(initialComment);
    setSelectedTag('');
    setAutoSegments(null);
    setConfirmAutoFillOpen(false);
    setTagMismatchError(null);
    onClose();
  }

  const handleTagSelected = (e) => {
    const tag = e.target.value;
    setSelectedTag(tag);
    const input = textFieldRef.current;
    if (!input) return;
    const { start, end } = savedSelectionRef.current;
    const newText = transcript.slice(0, start) + tag + transcript.slice(end);
    setTranscript(newText);
    setTimeout(() => {
      const pos = start + tag.length;
      input.setSelectionRange(pos, pos);
      input.focus({ preventScroll: true });
    }, 0);
    setTimeout(() => setSelectedTag(''), 100);
  };

  const parseSegTime = (mmss) => {
    const [mPart, sPart] = mmss.split(':');
    return parseFloat(mPart) * 60 + parseFloat(sPart);
  };

  const applyAutoFill = () => {
    if (!transcript) return;
    const EXCLUDED_TAGS = new Set(['pause', 'unintelligible', 'inaudible']);
    const TS = String.raw`\((\d{2}:\d{2}\.\d{3})\s*-\s*(\d{2}:\d{2}\.\d{3})\)\s*`;
    const tagRe = new RegExp(`(?:${TS})?(<[^/>][^>]*>|\\[[^\\]]+\\])`, 'g');

    const entries = [];
    for (const m of transcript.matchAll(tagRe)) {
      const [, tsStart, tsEnd, tag] = m;
      const inner = tag.replace(/^[<[]|[>\]]$/g, '').trim().toLowerCase();
      if (EXCLUDED_TAGS.has(inner)) continue;
      entries.push({ tag, tsStart, tsEnd });
    }

    if (!entries.length) return;
    const newSegs = entries.map(({ tag, tsStart, tsEnd }, i) => ({
      startTime: tsStart ? parseSegTime(tsStart) : i * DEFAULT_SEGMENT_DURATION,
      endTime:   tsEnd   ? parseSegTime(tsEnd)   : i * DEFAULT_SEGMENT_DURATION + DEFAULT_SEGMENT_DURATION,
      label: defaultLabel,
      tag,
    }));
    setAutoSegments(newSegs);
    setPlayerKey((k) => k + 1);

    // Timestamps now live on the segments; drop them from the editable transcript only.
    const stripped = transcript.replace(new RegExp(TS, 'g'), '');
    if (stripped !== transcript) setTranscript(stripped);
  };

  const handleAutoFill = () => {
    if (!transcript) return;
    if (segments.length > 0) {
      setConfirmAutoFillOpen(true);
      return;
    }
    applyAutoFill();
  };

  const handleConfirmAutoFill = () => {
    applyAutoFill();
    setConfirmAutoFillOpen(false);
  };

  const EXCLUDED_VALIDATE = new Set(['pause', 'unintelligible', 'inaudible']);

  const normalizeTagStr = (tag) =>
    tag ? tag.replace(/^[<\[]|[>\]]$/g, '').trim().toLowerCase() : '';

  const checkTagsMatch = () => {
    if (!transcript) return null;
    const txTags = [
      ...[...transcript.matchAll(/<([^/>][^>]*)>/g)].map((m) => m[1].trim().toLowerCase()),
      ...[...transcript.matchAll(/\[([^\]]+)\]/g)].map((m) => m[1].trim().toLowerCase()),
    ].filter((t) => !EXCLUDED_VALIDATE.has(t));
    const txSet = new Set(txTags);
    const segSet = new Set(segments.map((s) => normalizeTagStr(s.tag)).filter(Boolean));
    if (txSet.size === 0 && segSet.size === 0) return null;
    const missingInSegs = [...txSet].filter((t) => !segSet.has(t));
    const missingInTx = [...segSet].filter((t) => !txSet.has(t));
    if (missingInSegs.length === 0 && missingInTx.length === 0) return null;
    const parts = [];
    if (missingInSegs.length) parts.push(`Transcript tags without segments: ${missingInSegs.join(', ')}`);
    if (missingInTx.length) parts.push(`Segment tags not in transcript: ${missingInTx.join(', ')}`);
    return parts.join(' · ');
  };

  const handleSubmit = (fn) => () => {
    const err = checkTagsMatch();
    if (err) { setTagMismatchError(err); return; }
    setTagMismatchError(null);
    fn?.();
  };

  // Run auto-fill once when the modal opens on a task with no saved segments.
  // Component remounts per task (key={assignment_id} at call sites), so the
  // ref guarantees at most one run per task.
  const autoFilledOnLoadRef = useRef(false);
  useEffect(() => {
    if (!open || readOnly || autoFilledOnLoadRef.current) return;
    if ((initialSegments?.length ?? 0) > 0) return;
    autoFilledOnLoadRef.current = true;
    applyAutoFill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const playerTagOptions = useMemo(() => {
    const fromDb = Array.isArray(tagsData) ? tagsData.map((t) => t.tag_name).filter(Boolean) : [];
    const fromProp = (tagOptions ?? []).filter(Boolean);
    const merged = [...new Set([...transcriptionTags, ...fromDb, ...fromProp])];
    return merged.length > 0 ? merged : undefined;
  }, [tagsData, tagOptions, transcriptionTags]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      scroll="paper"
      disableEnforceFocus
      slotProps={DIALOG_SLOT_PROPS}
    >
      <Box
        className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f8f9ff] font-[Inter,system-ui,sans-serif] text-[#0d1c2e] antialiased dark:bg-gray-950 dark:text-gray-100"
        sx={{ height: '100%', minHeight: 0, maxHeight: '100dvh' }}
      >
        <SegHeader
          title={title}
          fileName={fileName}
          state={state}
          district={district}
          language={language}
          assignedTo={assignedToName}
          onClose={handleClose}
        />

        <Box
          component="main"
          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'stretch', md: 'flex-start' }, flexDirection: { xs: 'column', md: 'row' } }}>
            {open && audioFileURL && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SegmentationPlayer
                  key={playerKey}
                  audioFileURL={audioFileURL}
                  initialSegments={autoSegments ?? initialSegments}
                  onSegmentsChange={readOnly ? undefined : (s) => { setSegments(s); setTagMismatchError(null); }}
                  labels={labels}
                  tagOptions={playerTagOptions}
                  showLabels={showLabels}
                  readOnly={readOnly}
                />
              </Box>
            )}
            {open && transcription && (
              <Box sx={{ width: audioFileURL ? { xs: '100%', md: 320 } : '100%', flexShrink: { xs: 1, md: 0 }, display: 'flex', flexDirection: 'column', gap: 2, mt: audioFileURL ? { xs: 0, md: 8 } : 0, pr: { xs: 0, md: 1 }, pb: 2, px: { xs: 1, md: 0 } }}>
                {/* Second vendor pass — display only, never feeds the editable transcript */}
                {transcription2 && (
                  <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.secondary">
                      Vendor Transcription
                    </Typography>
                    <TextField
                      multiline
                      fullWidth
                      minRows={4}
                      maxRows={10}
                      variant="outlined"
                      value={transcription2}
                      slotProps={{ input: { readOnly: true } }}
                      sx={{ borderRadius: 2 }}
                    />
                  </Paper>
                )}
                {/* Original transcript (read-only) */}
                <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.secondary">
                    Original Transcription
                  </Typography>
                  <TextField
                    multiline
                    fullWidth
                    minRows={4}
                    maxRows={10}
                    variant="outlined"
                    value={transcription}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ borderRadius: 2 }}
                  />
                </Paper>
                <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2} sx={{ position: 'relative', minHeight: 36 }}>
                    <InfoRoundedIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Update Transcription
                    </Typography>
                    {!readOnly && (
                      <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Auto-populate from original transcription">
                          <Button size="small" variant="outlined" onClick={handleAutoFill}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1 }}>
                            Auto-fill
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </Stack>
                  <TextField
                    inputRef={textFieldRef}
                    multiline
                    fullWidth
                    minRows={10}
                    maxRows={20}
                    variant="outlined"
                    value={transcript}
                    onChange={(e) => { setTranscript(e.target.value); setTagMismatchError(null); }}
                    onBlur={(e) => {
                      savedSelectionRef.current = {
                        start: e.target.selectionStart ?? transcript.length,
                        end: e.target.selectionEnd ?? transcript.length,
                      };
                    }}
                    placeholder="Paste transcript content here..."
                    disabled={readOnly}
                    sx={{ borderRadius: 2, mb: 1 }}
                  />
                  {!readOnly && allTagOptions.length > 0 && (
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedTag}
                        onChange={handleTagSelected}
                        displayEmpty
                        MenuProps={{ disablePortal: true }}
                      >
                        <MenuItem value="" disabled>Insert tag</MenuItem>
                        {allTagOptions.map((tag) => (
                          <MenuItem key={tag} value={` ${tag} `}>{tag}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Paper>

                {/* Comment box */}
                <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.secondary">
                    Comments
                  </Typography>
                  <TextField
                    multiline
                    fullWidth
                    minRows={3}
                    maxRows={6}
                    variant="outlined"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add comments..."
                    disabled={readOnly}
                    sx={{ borderRadius: 2 }}
                  />
                </Paper>
              </Box>
            )}
          </Box>
        </Box>

        {tagMismatchError && (
          <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
            <Box
              role="alert"
              sx={{
                bgcolor: 'error.light',
                color: 'error.contrastText',
                borderRadius: 1,
                px: 2,
                py: 1,
                fontSize: '0.8125rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>Tag mismatch:</Box>
              {tagMismatchError}
            </Box>
          </Box>
        )}
        <SegFooter
          readOnly={readOnly}
          reviewMode={reviewMode}
          submitting={submitting}
          isPendingReview={isPendingReview}
          onClose={handleClose}
          onSaveLater={() => onSaveLater?.(segments, transcript, comment)}
          onSubmitNext={handleSubmit(() => onSubmitNext?.(segments, transcript, comment))}
          onSubmitClose={handleSubmit(() => onSubmitClose?.(segments, transcript, comment))}
          onApprove={() => onApprove?.(segments, transcript, comment)}
          onApproveNext={() => onApproveNext?.(segments, transcript, comment)}
          onReject={() => onReject?.(segments, transcript, comment)}
        />
      </Box>

      <Dialog
        open={confirmAutoFillOpen}
        onClose={() => setConfirmAutoFillOpen(false)}
        slotProps={{
          root: { sx: (theme) => ({ zIndex: theme.zIndex.modal + 100_001 }) },
        }}
      >
        <DialogTitle>Replace existing segments?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Auto-fill will replace the {segments.length} segment{segments.length === 1 ? '' : 's'} currently on the timeline. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAutoFillOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAutoFill} variant="contained" color="warning" sx={{ textTransform: 'none' }}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export function ReadOnlySegAnnotView({ open, onClose, row, workflowType }) {
  const isAnnotation = workflowType === 'ANNOTATION';

  const { data: savedSegmentsData } = useQuery({
    queryKey: ['ro-seg-view-segments', row?.assignment_id],
    queryFn: () => getSegments({ assignmentId: row.assignment_id }),
    enabled: open && !!row?.assignment_id,
    staleTime: Infinity,
  });

  const initialSegments = useMemo(() => {
    const raw = savedSegmentsData?.data ?? savedSegmentsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [savedSegmentsData]);

  const tagOptions = useMemo(
    () => [...new Set(initialSegments.map((s) => s.tag).filter(Boolean))],
    [initialSegments],
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll="paper"
      disableEnforceFocus
      slotProps={DIALOG_SLOT_PROPS}
    >
      <Box
        className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f8f9ff] font-[Inter,system-ui,sans-serif] text-[#0d1c2e] antialiased dark:bg-gray-950 dark:text-gray-100"
        sx={{ height: '100%', minHeight: 0, maxHeight: '100dvh' }}
      >
        <SegHeader
          title={isAnnotation ? 'Annotate Audio' : 'Segment Audio'}
          fileName={row?.file_name}
          state={row?.state}
          district={row?.district}
          language={row?.language}
          onClose={onClose}
        />

        <Box component="main" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {open && row?.file_path && (
            <SegmentationPlayer
              audioFileURL={row.file_path}
              initialSegments={initialSegments}
              labels={isAnnotation ? ANNOTATION_LABELS : SEGMENTATION_LABELS}
              showLabels={true}
              tagOptions={isAnnotation ? tagOptions : undefined}
              readOnly
            />
          )}
        </Box>

        <Box
          component="footer"
          className="flex shrink-0 items-center justify-end border-t border-[rgba(197,197,212,0.3)] bg-white px-4 py-2.5 shadow-[0_-1px_4px_rgba(0,0,0,0.04)] dark:border-gray-700 dark:bg-gray-900"
        >
          <Button
            className="ml-auto rounded bg-[#24389c] px-6 py-2 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[#1e3a8a]"
            sx={{ bgcolor: '#24389c', '&:hover': { bgcolor: '#1e3a8a' } }}
            onClick={onClose}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export function SegAnnotTextView({ open, onClose, row, workflowType, workflowId, assignedStatus, assignedToName, reviewer = false, onApprove, onApproveNext, onReject, isPending = false, tagOptions: externalTagOptions = [] }) {
  const isAnnotation = workflowType === 'ANNOTATION';
  const isReview = reviewer && assignedStatus === 'IN-REVIEW';

  const { data: fetchedSegmentsData } = useQuery({
    queryKey: ['segAnnotTextViewSegments', row?.assignment_id],
    queryFn: () => getSegments({ assignmentId: row.assignment_id }),
    enabled: open && !!row?.assignment_id,
    staleTime: Infinity,
  });

  const segments = useMemo(() => {
    const fromApi = fetchedSegmentsData?.data ?? fetchedSegmentsData ?? null;
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi;
    const rtr = row?.response_to_req;
    const raw = Array.isArray(rtr) ? rtr : (rtr?.segments ?? row?.segments ?? []);
    return Array.isArray(raw) ? raw : [];
  }, [fetchedSegmentsData, row]);

  const tagOptions = useMemo(
    () => [...new Set([
      ...(Array.isArray(externalTagOptions) ? externalTagOptions : []),
      ...segments.map((s) => s.tag).filter(Boolean),
    ])],
    [externalTagOptions, segments],
  );

  return (
    <SegmentationTool
      open={open}
      onClose={onClose}
      audioFileURL={row?.file_path}
      fileName={row?.file_name}
      state={row?.state}
      district={row?.district}
      language={row?.language}
      assignedToName={assignedToName}
      initialSegments={segments}
      transcription={row?.vendor_transcript ?? ''}
      updatedTranscription={row?.response_to_req?.updated_vendor_transcript ?? ''}
      initialComment={row?.response_to_req?.comment ?? ''}
      workflowId={workflowId}
      labels={isAnnotation ? ANNOTATION_LABELS : SEGMENTATION_LABELS}
      tagOptions={isAnnotation ? tagOptions : undefined}
      showLabels={false}
      title={isAnnotation ? 'Annotate Audio' : 'Segment Audio'}
      readOnly={!isReview}
      reviewMode={isReview}
      onApprove={onApprove}
      onApproveNext={onApproveNext}
      onReject={onReject}
      isPendingReview={isPending}
    />
  );
}
